import { inventoryFloorRefusal } from '../compile/inventory-facet';
import { inventoryHasPosition } from './inventory-support.js';
import { sceneWallNearBody } from './scene-wall-proximity.js';
// Compound walk and latched-object displacement share the bounded, rotated landing proof.
import { predictFall, type FallReaders, type FallVerdict } from './fall-check.js';
import { type RestShape, type Zone } from '../inventory/floor-move.js';
import { floorBandShape, liftDestinationClear, LiftUncertain } from '../inventory/lift-occupancy.js';
import { occupancyOverlap } from '../../../../ft3d/engine/ft3d-move-core.js';
import { physicalBodyNavigationShape } from '../../shared/physical-creature-body.js';
import { hardPassableAt, type WalkRouteMover, type WalkRouteObstacles, type CrossingHardObstacle } from './crossing-route.js';
import type { RouteWallWorld } from './route-walls.js';
import { legacyStepPositions } from '../zones/walk-zone-crossings.js';
import { encounterIsShapeless } from './exterior-ground.js';
import { statusItemIdOf } from './squeeze-at-rest.js';
import { squeezeBodyFt } from '../../../../ft3d/engine/ft3d-creature-size.js';
import { moverMovementInt } from './difficult-terrain.js';
import { encounterCreatureField, encounterField, inventoryField } from '../../shared/array-types.js';
import { getSwarmTemplateForCreature } from '../../shared/swarm-templates.js';
import { LATCHED_STATUS_ID, latchCanWalk, latchWalkSpeed, latchedCreatures, latchedObject, latchObjectId } from './latch-link.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const EPS = 1e-6;

export const LINK_STATUS_IDS = {
  riding: 'FF8D219A-2A83-0EE5-A222-E748753E8D0B', ridden: 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40',
  piloting: 'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12', piloted: 'DDC66C31-4093-84AF-18BB-093D924B8738',
  latched: LATCHED_STATUS_ID,
  grappling: 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD', grappled: '0C2A8037-B0BC-8300-C818-198881B00E7D',
} as const;
/** One creature-side status points at the inventory; the object carries no counterpart. */
export const DRAFT_LINK = { active: LATCHED_STATUS_ID } as const;
type LinkKey = keyof typeof LINK_STATUS_IDS;
const LINK_BY_ID = new Map(Object.entries(LINK_STATUS_IDS).map(([k, v]) => [v, k as LinkKey]));
export const draftLinkKind = (id: unknown): 'latched' | undefined => upper(id) === DRAFT_LINK.active ? 'latched' : undefined;
/** The ends of a link that MOVE (the mover's own walk drags the other end). */
const ACTIVE: ReadonlySet<LinkKey> = new Set(['grappling', 'ridden', 'piloting', 'latched']);
/** The follower end of each active link. */
const FOLLOWER_OF: Record<string, LinkKey> = { grappling: 'grappled', ridden: 'riding', piloting: 'piloted' };

export interface CompoundReaders extends FallReaders {
  /** The page's creature bodies for the member-landing test (size-tier base / collision height); null = not provable. */
  baseSizeFt: (row: Row) => number | null;
  heightFt: (row: Row) => number | null;
  crossingWorld?: () => RouteWallWorld | null;
  crossingObstacles?: (mover: WalkRouteMover) => WalkRouteObstacles;
  /** Canonical prop dimensions, including its physical height and rotation when held by the page. */
  propFootprint?: (row: Row) => (({ kind: 'circle'; r: number } | { kind: 'oblong'; halfW: number; halfD: number })
    & { heightFt?: number; facing?: number }) | null;
}
export type Point = { x: number; y: number; z: number };
export interface CompoundMember { id: string; inventoryId?: string; link: LinkKey; before: Point; after: Point; fall: FallVerdict | null }
export type CompoundPlan = { kind: 'none' } | { kind: 'move'; members: CompoundMember[] } | { kind: 'handback'; reason: string };
const hand = (reason: string): CompoundPlan => ({ kind: 'handback', reason: 'compound:' + reason });

const idsOf = (row: Row): string[] => {
  const out: string[] = [];
  const ec = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
  if (ec) out.push(ec);
  const inv = upper(inventoryField(row, 'inventory_id'));
  if (inv) out.push(inv);
  return out;
};
const linkKey = (status: Row): LinkKey | undefined => draftLinkKind(statusItemIdOf(status)) ?? LINK_BY_ID.get(upper(statusItemIdOf(status)) as never);

/** Normal Pilot is self-cast; older captures use reciprocal caster ids. Both share the connection. */
export function pilotedInventories(encounter: Row, mover: Row, read: Pick<FallReaders, 'statuses'>): Row[] {
  const moverId = upper(encounterCreatureField(mover.encounter_creatures, 'encounter_creature_id'));
  if (!moverId) return [];
  const pilots = read.statuses(mover).filter(s => linkKey(s) === 'piloting');
  if (!pilots.length) return [];
  return rows(encounter.encounter_inventories_arr).filter(row => {
    const id = upper(inventoryField(row, 'inventory_id'));
    return id && read.statuses(row).some(s => linkKey(s) === 'piloted' && upper(s.status_caster_id) === moverId
      && pilots.some(p => [moverId, id].includes(upper(p.status_caster_id)) && upper(p.status_connected_id) === upper(s.status_connected_id)));
  });
}

export interface InventoryDraftTeam { vehicle: Row; operator: Row | null; draggers: Row[] }
/** Latch membership is creature-side only; Pilot retains its existing reciprocal contract. */
export function inventoryDraftTeam(encounter: Row, mover: Row, read: Pick<FallReaders, 'statuses'>): InventoryDraftTeam | null {
  const creatures = rows(encounter.encounter_creatures_arr);
  const vehicle = pilotedInventories(encounter, mover, read)[0]
    ?? rows(encounter.encounter_inventories_arr).find(row => latchObjectId(row) === latchedObject(mover, read))
    ?? rows(encounter.encounter_inventories_arr).find(row => latchObjectId(row) === latchObjectId(mover));
  if (!vehicle) return null;
  const operator = creatures.find(row => pilotedInventories(encounter, row, read).includes(vehicle)) ?? null;
  const draggers = latchedCreatures(encounter, latchObjectId(vehicle), read);
  if (!draggers.length && !operator) return null;
  return { vehicle, operator, draggers };
}

/** ft_compound_actor_ids(): the mover's group — the undirected link graph's closure from the mover (its own ids included). */
export function compoundActorIds(encounter: Row, mover: Row, read: Pick<FallReaders, 'statuses'>): Set<string> {
  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string): void => {
    if (!a || !b || a === b) return;
    (adjacency.get(a) ?? adjacency.set(a, new Set()).get(a)!).add(b);
    (adjacency.get(b) ?? adjacency.set(b, new Set()).get(b)!).add(a);
  };
  for (const row of [...rows(encounter.encounter_creatures_arr), ...rows(encounter.encounter_inventories_arr)]) {
    const here = idsOf(row);
    for (const a of here) for (const b of here) link(a, b);
    for (const status of read.statuses(row)) {
      if (!linkKey(status)) continue;
      const partner = upper(status.status_caster_id);
      if (!/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(partner)) continue;
      for (const self of here) link(self, partner);
    }
  }
  const ids = new Set(idsOf(mover));
  const queue = [...ids];
  while (queue.length) {
    for (const next of adjacency.get(queue.shift()!) ?? []) if (!ids.has(next)) { ids.add(next); queue.push(next); }
  }
  return ids;
}

export interface CompoundInput {
  encounter: Row;
  shape: RestShape | null;
  mover: Row;
  origin: Point;
  destination: Point;
  /** The committed route, before translating it into each member's frame. */
  route?: ReadonlyArray<readonly number[]>;
  /** Teleports carry either end of a seat; ordinary passenger walks remain refused. */
  teleport?: boolean;
  /** Floor-move companions already carried by their native lock/body contract. */
  excludedCompanions?: ReadonlySet<string>;
}

/** A forced object displacement carries its whole latch team by the same vector. */
export function planLatchedObjectMove(encounter: Row, shape: RestShape | null, object: Row, destination: Point,
  read: CompoundReaders, excludedCompanions?: ReadonlySet<string>, route?: ReadonlyArray<readonly number[]>): CompoundPlan {
  if (!latchedCreatures(encounter, latchObjectId(object), read).length) return { kind: 'none' };
  const [x, y, z] = read.position(object), moverIds = new Set(idsOf(object));
  const group = compoundActorIds(encounter, object, read);
  for (const id of moverIds) group.delete(id);
  const team = inventoryDraftTeam(encounter, object, read);
  if (!team) return hand('inventory-member-not-held');
  return planDraftTeam({ encounter, shape, mover: object, origin: { x, y, z }, destination, excludedCompanions, route }, read, team, group, moverIds);
}
/** Plan the followers of an ACTIVE mover's walk from `origin` to `destination` (the committed stop). */
export function planCompoundWalk(input: CompoundInput, read: CompoundReaders): CompoundPlan {
  const floorRefusal = inventoryFloorRefusal(input.encounter);
  if (floorRefusal) return hand(floorRefusal);
  const { encounter, mover, origin, destination, shape } = input;
  const moverIds = new Set(idsOf(mover));
  const moverId = idsOf(mover)[0] ?? '';
  const group = compoundActorIds(encounter, mover, read);
  for (const id of moverIds) group.delete(id);
  if (group.size === 0) return { kind: 'none' };
  // The mover must be the ACTIVE end of every link it holds.
  const moverLinks = read.statuses(mover).map(linkKey).filter((k): k is LinkKey => !!k);
  if (moverLinks.some(k => !ACTIVE.has(k)) && !input.teleport) return hand('passenger-walk');
  const shapeless = !input.teleport && encounterIsShapeless(encounter);
  if ((!shape || !Array.isArray(shape.boxes)) && !shapeless) return hand('legacy-axes');
  const delta = { x: destination.x - origin.x, y: destination.y - origin.y, z: destination.z - origin.z };
  const creatures = rows(encounter.encounter_creatures_arr);
  const inventories = rows(encounter.encounter_inventories_arr);
  const draft = !input.teleport ? inventoryDraftTeam(encounter, mover, read) : null;
  if (draft?.draggers.length) {
    const draggers = draft.draggers.filter(row => latchCanWalk(row, read, latchWalkSpeed(row, read, row === draft.operator)));
    if (!draggers.length || draft.draggers.includes(mover) && !draggers.includes(mover)) return hand('no-walking-speed');
    // Non-walkers keep their latch but stay put and remain physical obstacles.
    const stationary = new Set(draft.draggers.filter(row => !draggers.includes(row)).flatMap(idsOf));
    const walkingLinks = { statuses: (row: Row) => read.statuses(row).filter(status => !linkKey(status)
      || !idsOf(row).some(id => stationary.has(id)) && !stationary.has(upper(status.status_caster_id))) };
    const walkingGroup = compoundActorIds(encounter, mover, walkingLinks);
    for (const id of moverIds) walkingGroup.delete(id);
    return planDraftTeam(input, read, { ...draft, draggers }, walkingGroup, moverIds, stationary);
  }
  const piloted = new Set(pilotedInventories(encounter, mover, read).map(row => upper(inventoryField(row, 'inventory_id'))));
  const members: CompoundMember[] = [];
  for (const id of group) {
    const inventory = inventories.find(row => upper(inventoryField(row, 'inventory_id')) === id);
    if (inventory) {
      if (input.teleport || !piloted.has(id)) return hand('inventory-member');
      const links = read.statuses(inventory).filter(s => linkKey(s));
      if (links.length !== 1) return hand('inventory-member');
      const [x, y, z] = read.position(inventory);
      members.push({ id, inventoryId: id, link: 'piloted', before: { x, y, z },
        after: { x: x + delta.x, y: y + delta.y, z: shapeless ? 0 : z + delta.z }, fall: null });
      continue;
    }
    const row = creatures.find(r => upper(record(r.encounter_creatures)?.encounter_creature_id) === id);
    if (!row) return hand('member-not-held');
    const links = read.statuses(row).map(s => ({ key: linkKey(s), partner: upper(s.status_caster_id) })).filter(l => l.key) as Array<{ key: LinkKey; partner: string }>;
    // A star: the member holds exactly one link, its partner is the mover, and the mover holds the matching active end.
    if (links.length !== 1 || links[0]!.partner !== moverId) return hand('chain');
    const key = links[0]!.key;
    const activeEnd = (Object.entries(FOLLOWER_OF).find(([, follower]) => follower === key)?.[0]
      ?? (input.teleport ? FOLLOWER_OF[key] : undefined)) as LinkKey | undefined;
    if (!activeEnd || !read.statuses(mover).some(s => linkKey(s) === activeEnd && upper(s.status_caster_id) === id)) return hand('link-mismatch');
    const [bx, by, bz] = read.position(row);
    const before = { x: bx, y: by, z: bz };
    // final_creature_move D1c: every member takes the mover's delta; FINAL GLUE seats a rider on its mount's cell.
    let after = { x: bx + delta.x, y: by + delta.y, z: bz + delta.z };
    if (shapeless && !input.teleport) after.z = 0;
    if (key === 'riding') after = { ...destination };
    if (input.teleport && key === 'ridden') after = { ...destination };
    let fall: FallVerdict | null = null;
    if (key === 'grappled') {
      // The overlap resolver re-places a grapplee (ft_resolve_move_destination, walk) and it runs its own fall: admit only a landing
      // the resolver keeps exactly — on the floor of a room holding the mover's destination, clear of every non-member body.
      const placed = exactMemberLanding(input, read, row, after, group, moverIds);
      if (!placed) return hand('member-destination');
      after = placed;
      // A shapeless walk rests on the canvas (the same support plane as its mover).
      fall = shapeless && Math.abs(after.z) <= EPS ? { kind: 'none', why: 'floor' }
        : predictFall({ row, at: after, encounter, shape, grappler: { ...mover, position: { x_pos: destination.x, y_pos: destination.y, z_pos: destination.z } } }, read);
      if (fall.kind === 'handback') return hand('member-' + fall.reason);
      if (fall.kind === 'fall') return hand('member-fall');
    }
    members.push({ id, link: key, before, after, fall });
  }
  members.sort((a, b) => a.id.localeCompare(b.id));
  return { kind: 'move', members };
}

function planDraftTeam(input: CompoundInput, read: CompoundReaders, team: InventoryDraftTeam,
  group: ReadonlySet<string>, moverIds: ReadonlySet<string>, stationary?: ReadonlySet<string>): CompoundPlan {
  // Drafts carry rigged operators too. Use the same banked physical cylinder as the walk's own collision proof.
  const memberRead: CompoundReaders = { ...read, baseSizeFt: row =>
    getSwarmTemplateForCreature(row as never) ? null : read.geometry(row)?.diameter ?? null };
  const participants = [...new Set([team.vehicle, ...(team.operator ? [team.operator] : []), ...team.draggers])]
    .filter(row => !idsOf(row).some(id => stationary?.has(id)));
  const delta = { x: input.destination.x - input.origin.x, y: input.destination.y - input.origin.y,
    z: input.destination.z - input.origin.z };
  const vehicleId = latchObjectId(team.vehicle), operatorId = team.operator ? idsOf(team.operator)[0] : '';
  const independent: CompoundReaders = { ...read, statuses: row => read.statuses(row).filter(status => {
    const key = linkKey(status), self = idsOf(row)[0], partner = upper(status.status_caster_id);
    if (key && (stationary?.has(self ?? '') || stationary?.has(partner))) return false;
    return key !== 'latched' && !(key === 'piloting' && self === operatorId && [operatorId, vehicleId].includes(partner))
      && !(key === 'piloted' && self === vehicleId && partner === operatorId);
  }) };
  const extra = new Map<string, CompoundMember>();
  const vehicleLinks = read.statuses(team.vehicle).map(linkKey).filter(Boolean);
  if (vehicleLinks.join(',') !== (team.operator ? 'piloted' : '')) return hand('chain');
  // A draft can still carry its rider or an ordinary grapplee. Reuse the existing
  // transport planner with only the team's own inventory edges removed.
  for (const row of participants) {
    if (row === team.vehicle) continue;
    const [x,y,z] = read.position(row);
    const after = { x: x + delta.x, y: y + delta.y, z: encounterIsShapeless(input.encounter) ? 0 : z + delta.z };
    const nested = planCompoundWalk({ ...input, mover: row, origin: {x,y,z}, destination: after,
      excludedCompanions: new Set([...(input.excludedCompanions ?? []), ...group, ...moverIds]),
      route: input.route?.map(point => [x + point[0]! - input.origin.x, y + point[1]! - input.origin.y, after.z]) }, independent);
    if (nested.kind === 'handback') return nested;
    if (nested.kind === 'move') for (const member of nested.members) {
      if (member.inventoryId) return hand('chain');
      extra.set(member.id, member);
    }
  }
  const allowed = new Set([...participants.flatMap(idsOf), ...extra.keys()]);
  if ([...group].some(id => !allowed.has(id))) return hand('chain');
  const members: CompoundMember[] = [];
  const route = legacyStepPositions(input.route ?? [[input.origin.x, input.origin.y], [input.destination.x, input.destination.y]]);
  for (const row of participants) {
    const id = idsOf(row)[0]!;
    const [x, y, z] = read.position(row), before = { x, y, z };
    const after = { x: x + delta.x, y: y + delta.y, z: encounterIsShapeless(input.encounter) ? 0 : z + delta.z };
    const inventory = row === team.vehicle;
    const path = route.map(([rx, ry]) => ({ x: x + rx - input.origin.x, y: y + ry - input.origin.y, z: after.z }));
    if (!exactMemberLanding(input, memberRead, row, after, group, moverIds, path, inventory)) return hand('member-destination');
    if (moverIds.has(id)) continue;
    members.push({ id, ...(inventory ? { inventoryId: id } : {}),
      link: inventory ? 'latched' : row === team.operator ? 'piloting' : 'latched', before, after, fall: null });
  }
  for (const member of extra.values()) if (!participants.some(row => idsOf(row).includes(member.id))) members.push(member);
  members.sort((a, b) => a.id.localeCompare(b.id));
  return { kind: 'move', members };
}

function exactMemberLanding(input: CompoundInput, read: CompoundReaders, row: Row, at: Point, group: ReadonlySet<string>, moverIds: ReadonlySet<string>,
  path: readonly Point[] = [at], inventory = false): Point | null {
  const footprint = inventory ? read.propFootprint?.(row) : null;
  const base = footprint ? 2 * (footprint.kind === 'circle' ? footprint.r : Math.hypot(footprint.halfW, footprint.halfD)) : read.baseSizeFt(row);
  const height = footprint?.heightFt ?? read.heightFt(row);
  if (base === null || height === null || ![base, height].every(n => Number.isFinite(n) && n > 0)
    || (inventory && read.propFootprint && !footprint)) return null;
  const r = base / 2, excluded = new Set([...group, ...moverIds, ...(input.excludedCompanions ?? [])]);
  const zoneAt = (p: Point): Zone => footprint?.kind === 'oblong'
    ? { ...p, kind: 'oblong', halfW: footprint.halfW, halfD: footprint.halfD, facing: footprint.facing ?? 0, zMin: p.z, zMax: p.z + height }
    : { ...p, kind: 'circle', r, zMin: p.z, zMax: p.z + height };
  const world = read.crossingWorld?.() ?? { shape: input.shape,
    canvasExterior: Number(scalar(encounterField(input.encounter, 'encounter_inside')) ?? 0) !== 1,
    overlayUnknown: !!input.shape?.openings?.length };
  const shape = world.shape && (footprint?.kind === 'oblong' || read.geometry(row)?.rulesDiameter !== undefined)
    ? physicalBodyNavigationShape(world.shape as never, height) as unknown as RestShape : world.shape;
  const [bx, by, bz] = read.position(row), before = { x: bx, y: by, z: bz };
  // The existing router bounds unknown props to this member's swept footprint, rather than its 40-ft A* search plane.
  const obstacles = read.crossingObstacles?.({ tierFt: inventory ? null : read.geometry(row)?.rulesDiameter ?? base, compoundIds: excluded, zMin: at.z, zMax: at.z + height,
    searchRegion: { structural: !!shape, rayZ: at.z + height / 2, radius: r, plane: { boxes: path.map(p => ({
      id: 'compound-foot', x: { min: p.x, max: p.x }, y: { min: p.y, max: p.y }, floor: p.z, ceiling: p.z + height })) } } });
  if (obstacles && 'uncertain' in obstacles) return null;
  const props: CrossingHardObstacle[] = obstacles?.props ?? [];
  // Callers without the router reader still use held geometry, never a fixed proximity veto.
  if (!obstacles) for (const inv of rows(input.encounter.encounter_inventories_arr)) {
    if (excluded.has(upper(inventoryField(inv, 'inventory_id'))) || Number(inventoryField(inv, 'inventory_deleted') ?? 0) === 1
      || !inventoryHasPosition(inv, read.position)) continue;
    const f = read.propFootprint?.(inv), g = read.geometry(inv), [x, y, z] = read.position(inv);
    if ((read.propFootprint && !f) || (!f && !g)) return null;
    const physical: Zone | null = f?.kind === 'oblong' ? { kind: 'oblong', x, y, halfW: f.halfW, halfD: f.halfD,
      facing: f.facing ?? 0, zMin: z, zMax: z + (f.heightFt ?? g?.height ?? 0) } : null;
    props.push({ id: upper(inventoryField(inv, 'inventory_id')), x, y, z, r: f?.kind === 'circle' ? f.r : g?.diameter ? g.diameter / 2 : 0, physical, banded: true });
  }
  const onFloor = (p: Point, candidates = props): CrossingHardObstacle[] => candidates.filter(o => !o.banded || !shape
    || Math.abs((floorBandShape(shape, o.x, o.y, o.z)?.floor ?? 0) - (floorBandShape(shape, p.x, p.y, p.z)?.floor ?? 0)) <= 0.01);
  try {
    for (const p of path) {
      if (shape) {
        const floor = floorBandShape(shape, p.x, p.y, p.z)?.floor ?? 0;
        // An unavailable door overlay matters only at a wall the body can touch, as with the router's bounded prop set.
        const localShape = world.overlayUnknown ? { ...shape,
          boxes: shape.boxes.filter(box => Math.hypot(Math.max(0, box.x.min - p.x, p.x - box.x.max),
            Math.max(0, box.y.min - p.y, p.y - box.y.max)) <= r + 0.5),
          sceneWalls: rows(shape.sceneWalls).filter(wall => sceneWallNearBody(wall, p, r, height)) } : shape;
        if (Math.abs(p.z - floor) > 0.01 || !liftDestinationClear({ ...world, shape: localShape,
          persisted: before, footprintAt: (x, y, z) => zoneAt({ x, y, z }), heightFt: height, tierFt: base, obstacles: () => [],
          allowSqueeze: !inventory && read.geometry(row)?.rulesDiameter === undefined,
          squeezeSizeFt: squeezeBodyFt(base, moverMovementInt(row, read, 'squeeze_size_category_offset')) }, p.x, p.y, p.z)) return null;
      } else if (!encounterIsShapeless(input.encounter) || Math.abs(p.z) > EPS) return null;
      if (!hardPassableAt(onFloor(p), [before.x, before.y], r, p.x, p.y,
        footprint?.kind === 'oblong' ? (x, y) => zoneAt({ x, y, z: p.z }) : undefined)) return null;
    }
  } catch (error) { if (error instanceof LiftUncertain) return null; throw error; }
  // Passage props can be crossed, but remain standing obstacles at the exact destination.
  const landing = read.crossingObstacles?.({ tierFt: inventory ? null : read.geometry(row)?.rulesDiameter ?? base, compoundIds: excluded, zMin: at.z, zMax: at.z + height, landing: true,
    searchRegion: { structural: !!shape, rayZ: at.z + height / 2, radius: r, plane: { boxes: [{
      x: { min: at.x, max: at.x }, y: { min: at.y, max: at.y }, floor: at.z, ceiling: at.z + height }] } } });
  if (landing && 'uncertain' in landing) return null;
  // A walk may escape an existing prop overlap, but the exact landing must be free.
  if (onFloor(at, landing?.props ?? props).some(o => occupancyOverlap(zoneAt(at), o.physical ?? {
    kind: 'circle', x: o.x, y: o.y, r: o.r, zMin: at.z, zMax: at.z + height }))) return null;
  for (const other of rows(input.encounter.encounter_creatures_arr)) {
    const id = upper(encounterCreatureField(other.encounter_creatures, 'encounter_creature_id'));
    if (!id || excluded.has(id)) continue;
    if (Number(scalar(encounterCreatureField(other.encounter_creatures, 'encounter_creature_private')) ?? 0) === 1) continue;
    const ob = read.baseSizeFt(other), oh = read.heightFt(other);
    if (ob === null || oh === null || ![ob, oh].every(n => Number.isFinite(n) && n > 0)) return null;
    const [ox, oy, oz] = read.position(other);
    if (occupancyOverlap(zoneAt(at), { kind: 'circle', x: ox, y: oy, r: ob / 2, zMin: oz, zMax: oz + oh })) return null;
  }
  return at;
}
